//src/components/SearchChatModal.jsx

import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { FiRefreshCw, FiEdit, FiEye } from "react-icons/fi";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const SearchChatModal = ({ onSelectChat, onSelectVision, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState([]); // unified results: chat + vision
  const [isRefreshing, setIsRefreshing] = useState(false);

  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const refreshOffTimerRef = useRef(null);
  const latestRunRef = useRef(0);
  const itemsRef = useRef([]);
  const didMountRef = useRef(false);
  const didInitialFetchRef = useRef(false);

  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // ----------------------------
  // ✅ Title formatting (NO MORE "Chat on ...")
  // We match Sidebar behavior:
  // - If backend legacy default is "Chat on January 29, 2026"
  //   display as "Chat 0129-2026"
  // - Otherwise show the saved title as-is
  // ----------------------------

  const pad2 = (n) => String(n).padStart(2, "0");

  const coerceToDate = (ts) => {
    try {
      if (!ts) return null;
      if (ts instanceof Date) return ts;
      if (typeof ts.toDate === "function") return ts.toDate(); // Firestore Timestamp
      if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
      if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
      return null;
    } catch {
      return null;
    }
  };

  const formatChatMMDDYYYY_NO_ON = (d) => {
    try {
      if (!d || isNaN(d.getTime?.())) return null;
      const mm = pad2(d.getMonth() + 1);
      const dd = pad2(d.getDate());
      const yyyy = d.getFullYear();
      // ✅ "Chat 0129-2026"
      return `Chat ${mm}${dd}-${yyyy}`;
    } catch {
      return null;
    }
  };

  const tryParseLegacyChatOn = (name) => {
    try {
      if (!name || typeof name !== "string") return null;
      if (!/^chat on\s+/i.test(name)) return null;
      const raw = name.replace(/^chat on\s+/i, "").trim();
      const dt = new Date(raw);
      if (isNaN(dt.getTime())) return null;
      return dt;
    } catch {
      return null;
    }
  };

  const getChatTitleDisplay = (rawChat) => {
    try {
      const raw = (rawChat?.session_name || rawChat?.title || "Untitled Chat").toString();

      // ✅ Only transform the legacy "Chat on ..." style
      if (/^chat on\s+/i.test(raw)) {
        const d1 = coerceToDate(rawChat?.created_at);
        const fmt1 = formatChatMMDDYYYY_NO_ON(d1);
        if (fmt1) return fmt1;

        const d2 = tryParseLegacyChatOn(raw);
        const fmt2 = formatChatMMDDYYYY_NO_ON(d2);
        if (fmt2) return fmt2;
      }

      // Already renamed or already in "Chat 0129-2026" form
      return raw || "Untitled Chat";
    } catch {
      return "Untitled Chat";
    }
  };

  // ----------------------------
  // ✅ Keyword robustness helpers
  // - Handles extra spaces, punctuation, partial tokens (doc -> document)
  // - Adds lightweight "close match" tolerance for small typos
  // - Ranked results (exact phrase > all tokens > prefix > fuzzy)
  // - Still keeps UI identical (only ordering/filtering logic)
  // ----------------------------

  const normalizeText = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .replace(/[\u2019']/g, "'")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const expandTokens = (tokens) => {
    try {
      const out = new Set();
      for (const tok of tokens || []) {
        const t = (tok || "").trim();
        if (!t) continue;
        out.add(t);

        // Simple stem variants (keeps it lightweight + predictable)
        if (t.length >= 4 && t.endsWith("s")) out.add(t.slice(0, -1));
        if (t.length >= 5 && t.endsWith("es")) out.add(t.slice(0, -2));
        if (t.length >= 5 && t.endsWith("ies")) out.add(t.slice(0, -3) + "y");
      }
      return Array.from(out);
    } catch {
      return tokens || [];
    }
  };

  const tokenize = (s) => {
    const t = normalizeText(s);
    const base = t ? t.split(" ").filter(Boolean) : [];
    return expandTokens(base);
  };

  // ✅ Tiny, early-exit Levenshtein (for "close" matches)
  const levenshteinWithin = (a, b, maxDist) => {
    try {
      if (a === b) return true;
      if (!a || !b) return false;

      const la = a.length;
      const lb = b.length;

      if (Math.abs(la - lb) > maxDist) return false;

      // Ensure a is the shorter
      if (la > lb) return levenshteinWithin(b, a, maxDist);

      // DP with early exit
      const prev = new Array(lb + 1);
      const curr = new Array(lb + 1);

      for (let j = 0; j <= lb; j++) prev[j] = j;

      for (let i = 1; i <= la; i++) {
        curr[0] = i;
        let rowMin = curr[0];

        const ca = a.charCodeAt(i - 1);

        for (let j = 1; j <= lb; j++) {
          const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;

          const ins = curr[j - 1] + 1;
          const del = prev[j] + 1;
          const sub = prev[j - 1] + cost;

          const v = Math.min(ins, del, sub);
          curr[j] = v;
          if (v < rowMin) rowMin = v;
        }

        if (rowMin > maxDist) return false;

        for (let j = 0; j <= lb; j++) prev[j] = curr[j];
      }

      return prev[lb] <= maxDist;
    } catch {
      return false;
    }
  };

  const fuzzyTokenPresent = (tok, words) => {
    try {
      if (!tok || tok.length < 3) return false;

      // Keep it conservative/fast
      const maxDist = tok.length <= 5 ? 1 : 2;

      for (const w of words) {
        if (!w) continue;
        // avoid super short words
        if (w.length < 3) continue;

        // only compare reasonably similar lengths
        if (Math.abs(w.length - tok.length) > maxDist) continue;

        if (levenshteinWithin(tok, w, maxDist)) return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const scoreMatch = (item, term) => {
    // Higher score = better
    const phrase = normalizeText(term);
    if (!phrase) return 0;

    const tokens = tokenize(phrase);
    if (!tokens.length) return 0;

    const title = normalizeText(item?.title || "");
    const preview = normalizeText(item?.preview || "");
    const hay = `${title} ${preview}`.trim();

    const hayWords = hay.split(" ").filter(Boolean);
    const titleWords = title.split(" ").filter(Boolean);
    const previewWords = preview.split(" ").filter(Boolean);

    const tokenHit = (tok) =>
      hay.includes(tok) || hayWords.some((w) => w.startsWith(tok)) || fuzzyTokenPresent(tok, hayWords);

    // ✅ If NONE of the tokens are present at all (or as prefix/fuzzy), reject
    const anyTokenPresent = tokens.some((tok) => tokenHit(tok));
    if (!anyTokenPresent) return -1;

    let score = 0;

    // A) Exact phrase bonus
    if (title.includes(phrase)) score += 400;
    if (preview.includes(phrase)) score += 180;

    // B) All tokens present — allow prefix/fuzzy satisfaction too
    const allTokensPresent = tokens.every((tok) => tokenHit(tok));
    if (allTokensPresent) score += 220;

    // Token weighting (title > preview)
    for (const tok of tokens) {
      const inTitle = title.includes(tok) || titleWords.some((w) => w.startsWith(tok)) || fuzzyTokenPresent(tok, titleWords);
      const inPreview =
        preview.includes(tok) || previewWords.some((w) => w.startsWith(tok)) || fuzzyTokenPresent(tok, previewWords);

      if (inTitle) score += 35;
      if (inPreview) score += 10;

      // Extra bump for prefix/fuzzy when exact is missing (helps "come close")
      if (!hay.includes(tok)) {
        const hasPrefix = hayWords.some((w) => w.startsWith(tok));
        if (hasPrefix) score += 35;

        const hasFuzzy = fuzzyTokenPresent(tok, hayWords);
        if (hasFuzzy) score += 28;
      }
    }

    // Starts-with bonus
    if (title.startsWith(phrase)) score += 120;

    // Slight preference for shorter titles (less noise)
    score += Math.max(0, 18 - Math.min(18, title.length / 12));

    return score;
  };

  const normalizeChat = (c) => {
    const displayTitle = getChatTitleDisplay(c);
    return {
      kind: "chat",
      id: c?.session_id || "",
      session_id: c?.session_id || "",
      title: displayTitle,
      preview: (c?.preview || c?.last_message || "").trim(),
      raw: c,
    };
  };

  const normalizeVision = (v, idx) => ({
    kind: "vision",
    id: v?.session_id || `vision-${idx}`,
    session_id: v?.session_id || "",
    title: v?.session_name || `Vision ${idx + 1}`,
    preview: (v?.preview || "").trim(),
    raw: v,
  });

  const fetchWithRetryJson = async (url, opts, retries = 1) => {
    let lastErr = null;
    for (let i = 0; i <= retries; i++) {
      try {
        const res = await fetch(url, opts);
        const data = await res.json().catch(() => ({}));
        return { res, data };
      } catch (e) {
        lastErr = e;
        // Abort should not retry
        if (String(e?.name || "").toLowerCase() === "aborterror") throw e;
      }
    }
    throw lastErr;
  };

  const fetchSearchResults = useCallback(async () => {
    if (!currentUser) {
      setItems([]);
      return;
    }

    // ✅ Track this run so older runs can’t flip the spinner off/on (“jitter”)
    const runId = ++latestRunRef.current;

    // Cancel any in-flight request (prevents flicker + out-of-order results)
    try {
      abortRef.current?.abort?.();
    } catch {}
    const controller = new AbortController();
    abortRef.current = controller;

    // ✅ Clear any pending "turn spinner off" timer from a prior run
    try {
      if (refreshOffTimerRef.current) clearTimeout(refreshOffTimerRef.current);
    } catch {}
    refreshOffTimerRef.current = null;

    setIsRefreshing(true);

    const prevItems = itemsRef.current; // keep previous list on transient errors

    try {
      const token = await currentUser.getIdToken();
      const term = searchTerm || "";

      const chatUrl = `${HOST}/chat/search`;
      const visionUrl = `${HOST}/vision/sessions/list`;

      const [chatRes, visionRes] = await Promise.allSettled([
        fetchWithRetryJson(
          chatUrl,
          {
            method: "POST",
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            // ✅ send raw user term (backend should handle), client is robust anyway
            body: JSON.stringify({ search_term: term }),
          },
          1
        ),
        fetchWithRetryJson(
          visionUrl,
          {
            method: "POST",
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              search_term: term,
              include_archived: true, // search should find archived too
              include_deleted: false, // never show deleted
              include_preview: true,
              limit: 200,
              order: "desc",
            }),
          },
          1
        ),
      ]);

      let chats = [];
      let visions = [];

      if (chatRes.status === "fulfilled") {
        const { res, data } = chatRes.value || {};
        if (res?.ok && Array.isArray(data?.results)) {
          chats = data.results.map(normalizeChat).filter((x) => x.session_id);
        }
      }

      if (visionRes.status === "fulfilled") {
        const { res, data } = visionRes.value || {};
        if (res?.ok && Array.isArray(data?.results)) {
          visions = data.results
            .filter((v) => !v?.deleted) // extra safety
            .map((v, idx) => normalizeVision(v, idx))
            .filter((x) => x.session_id);
        }
      }

      // Merge: chats first, then vision (matches Sidebar)
      const merged = [...chats, ...visions];

      // ✅ Ranked keyword filtering (space/punctuation tolerant + partial token + fuzzy support)
      const phrase = normalizeText(term);
      if (phrase) {
        const scored = merged
          .map((x) => ({ x, s: scoreMatch(x, phrase) }))
          .filter((p) => p.s >= 0)
          .sort((a, b) => b.s - a.s)
          .map((p) => p.x);

        setItems(scored);
      } else {
        setItems(merged);
      }
    } catch (err) {
      // Ignore abort errors
      if (String(err?.name || "").toLowerCase() !== "aborterror") {
        console.error("❌ Search error:", err);
        // ✅ Keep previous results instead of flashing empty (reduces “refresh” feeling)
        setItems(prevItems || []);
      } else {
        // Abort: do nothing
      }
    } finally {
      // ✅ Only the latest run is allowed to stop the spinner
      if (runId !== latestRunRef.current) return;

      // ✅ Keep a tiny minimum spin window (smooth), but do not “re-jitter”
      refreshOffTimerRef.current = setTimeout(() => {
        if (runId !== latestRunRef.current) return;
        setIsRefreshing(false);
      }, 350);
    }
  }, [currentUser, searchTerm]);

  // ✅ FIX: On open (and when currentUser becomes available), auto-fetch once immediately
  useEffect(() => {
    if (!currentUser) return;
    if (didInitialFetchRef.current) return;
    didInitialFetchRef.current = true;

    // Pull everything right away (no need for user to press refresh)
    fetchSearchResults();
  }, [currentUser, fetchSearchResults]);

  // Debounced search (reduces flicker + backend spam)
  // ✅ Skip the very first mount tick (initial load is handled by the effect above)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (!currentUser) return;

    const t = setTimeout(() => {
      fetchSearchResults();
    }, 220);

    return () => clearTimeout(t);
  }, [searchTerm, fetchSearchResults, currentUser]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  useEffect(() => {
    return () => {
      try {
        abortRef.current?.abort?.();
      } catch {}
      try {
        if (refreshOffTimerRef.current) clearTimeout(refreshOffTimerRef.current);
      } catch {}
      refreshOffTimerRef.current = null;
    };
  }, []);

  const handleClickOutside = (e) => {
    if (e.target.className === "modal-overlay") {
      onClose?.();
    }
  };

  const handleSelect = (item) => {
    if (!item) return;

    if (item.kind === "vision") {
      if (typeof onSelectVision === "function") {
        onSelectVision(item.raw);
      } else if (typeof onSelectChat === "function") {
        // backward-safe: parent can detect kind if they want
        onSelectChat({ ...item.raw, __kind: "vision" });
      }
      onClose?.();
      return;
    }

    // regular chat
    if (typeof onSelectChat === "function") {
      onSelectChat(item.raw);
    }
    onClose?.();
  };

  return (
    <div
      className="modal-overlay"
      style={styles.overlay}
      onClick={handleClickOutside}
      role="dialog"
      aria-modal="true"
      aria-label="Search Chats"
    >
      <div style={getResponsiveModalStyle()}>
        <div style={styles.topBar}>
          <h3 style={styles.title}>Search Chats</h3>
          <div style={styles.iconGroup}>
            <button
              onClick={fetchSearchResults}
              aria-label="Refresh"
              style={{
                ...styles.iconBtn,
                ...(isRefreshing ? { ...styles.spin, transition: "none" } : {}),
              }}
            >
              <FiRefreshCw size={20} />
            </button>
            <button onClick={onClose} style={styles.iconBtn} aria-label="Close">
              ×
            </button>
          </div>
        </div>

        {!currentUser ? (
          <p style={styles.noMatch}>Please log in to view your chats.</p>
        ) : (
          <>
            <div style={styles.inputContainer}>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search by keyword or title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.input}
              />
            </div>

            {/* ✅ IMPORTANT: list now fills remaining modal height (no wasted bottom space) */}
            <div style={styles.list}>
              {items.map((item) => (
                <div key={item.id} style={styles.item} onClick={() => handleSelect(item)}>
                  <div style={styles.itemTopRow}>
                    <span style={styles.itemIcon}>{item.kind === "vision" ? <FiEye /> : <FiEdit />}</span>
                    <div style={styles.itemTitle} title={item.title}>
                      {item.title}
                    </div>
                  </div>

                  <div style={styles.preview}>{item.preview || "No preview available."}</div>
                </div>
              ))}

              {items.length === 0 && <p style={styles.noMatch}>No matches found.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },

  // ✅ Modal is a flex column, so the list can grow and scroll (no blank space)
  modalBase: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
    position: "relative",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden", // prevent whole-modal scroll; list handles scrolling
  },

  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    flex: "0 0 auto",
  },
  title: {
    fontSize: "18px",
    fontWeight: "600",
    margin: 0,
  },
  iconGroup: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
  iconBtn: {
    background: "none",
    border: "none",
    fontSize: "26px",
    cursor: "pointer",
    color: "#333",
    transition: "transform 0.3s ease",
  },
  spin: {
    animation: "spin 0.6s linear",
  },

  inputContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "15px",
    flex: "0 0 auto",
  },
  input: {
    width: "100%",
    maxWidth: "400px",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  // ✅ Fills remaining height; scroll is inside list (right-side scrollbar)
  list: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    scrollBehavior: "smooth",
  },

  item: {
    padding: "10px",
    borderBottom: "1px solid #eee",
    cursor: "pointer",
  },
  itemTopRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
  },
  itemIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "18px",
    minWidth: "18px",
    color: "#333",
  },
  itemTitle: {
    fontWeight: "bold",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
  },
  preview: {
    fontSize: "13px",
    color: "#666",
    marginTop: "4px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  noMatch: {
    textAlign: "center",
    color: "#888",
    padding: "10px",
  },
};

const getResponsiveModalStyle = () => {
  const width = window.innerWidth;
  const isMobileOrTablet = width < 1025;
  return {
    ...styles.modalBase,
    width: isMobileOrTablet ? "100vw" : "90%",
    maxWidth: isMobileOrTablet ? "100%" : "500px",
    height: isMobileOrTablet ? "100vh" : "80vh",
    borderRadius: isMobileOrTablet ? "0px" : "10px",
  };
};

if (!document.getElementById("spin-keyframes")) {
  const style = document.createElement("style");
  style.id = "spin-keyframes";
  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

export default SearchChatModal;
